/**
 * YOONITE application server
 *
 * (c) 2019 FSZ GmbH
 * All rights reserved.
 */
import {enumerate} from 'ts-transformer-enumerate';

import '~/shared/src/types/yn.d';
import Joi from '~/shared/src/utilities/joiExtended';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import {DataDescriptor as ColumnDataDescriptor} from '~/shared/src/utilities/dataStructure';
import {conditionOperator} from '~/shared/src/utilities/condition';
import {EditorConfig} from '~/shared/src/utilities/editorConfig';
import {FilterConfig} from '~/shared/src/utilities/filterConfig';
import {
    Format,
    formatTypes
} from '~/shared/src/utilities/format';
import {Message} from '~/shared/src/utilities/message';
import {Style} from '~/shared/src/utilities/style';

import {ColumnPassThroughOptions} from 'primereact/column';

export const searchStringSchema = Joi.string()
                                     .empty( '' )
                                     .default( '' )
                                     .description( 'The search string for selecting items. The search string is used on key and name of the item.' );

export const showPropertyDefaultsSchema = Joi.boolean()
                                             .default( true )
                                             .description( 'Should property defaults shown.' );

export const rawModeSchema = Joi.boolean()
                                .default( false )
                                .description( 'Should all information shown as stored in the database or not.' );

export const showAdditionalRawNamesSchema = Joi.boolean()
                                               .default( false )
                                               .description( 'Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.<br/>This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)<br/><br/>Only applicable if <strong>rawMode</strong> is set to false.' );

export const pageSchema = Joi.number()
                             .default( 1 )
                             .description( 'The displayed result page.<br/><br/>Only applicable if <strong>search</strong> is set to true.' );

export const rowsSchema = Joi.number()
                             .default( 50 )
                             .description( 'The count of rows per result page.' );

export const searchSchema = Joi.boolean()
                               .default( false )
                               .description( 'Should the overview load a result initially.' );

export const forceFitSchema = Joi.boolean()
                                 .default( false )
                                 .description( 'Should the columns fit to the screen size initially.' );

export const editableSchema = Joi.boolean()
                                 .default( false )
                                 .description( 'Is the overview generally editable.' );

export const editModeSchema = Joi.boolean()
                                 .default( false )
                                 .description( 'Is the overview in edit mode.' );

export type DataDescriptor = {
    key? : string;
    field : string;
};
export const dataDescriptorSchema = Joi.object( {
                                           key  : Joi.string()
                                                     .description( 'The unique key for this column.' ),
                                           field: Joi.string()
                                                     .required()
                                                     .description( 'The key within the loaded data.' )
                                       } )
                                       .description( 'The descriptor for the data.' );
export type OptionalDataDescriptor = {
    key? : string;
    field? : string;
};
export const optionalDataDescriptorSchema = Joi.object( {
                                                   key  : Joi.string()
                                                             .description( 'The unique key for this column.' ),
                                                   field: Joi.string()
                                                             .description( 'The key within the loaded data.' )
                                               } )
                                               .description( 'The descriptor for the data.' );

export type FilterMatchMode = 'startsWith' | 'notStartsWith' | 'contains' | 'notContains' | 'endsWith' | 'notEndsWith' | 'equals' | 'notEquals' | 'in' | 'notIn' | 'lt' | 'lte' | 'gt' | 'gte' | 'empty' | 'notEmpty' | 'custom';
export type FilterValueType = PropertyValueType | 'REFTYPEWITHDIRECTION' | 'REFDIRECTION' | 'STATUS' | 'STATUSID' | 'STATUSNAME';
export type FilterDescriptor = {
    operator : Exclude<FilterMatchMode, 'empty' | 'notEmpty'>;
    value : string;
    valueType? : FilterValueType;
} | {
    operator : Extract<FilterMatchMode, 'empty' | 'notEmpty'>;
    value? : never;
    valueType? : never;
};
export const filterMatchModes = enumerate<FilterMatchMode>();
export const filterValueTypes = enumerate<FilterValueType>();
export const filterDescriptorSchema = Joi.object( {
                                             operator : Joi.string()
                                                           .required()
                                                           .valid( ...filterMatchModes )
                                                           .description( 'The operator for matching the filter value with.' ),
                                             value    : Joi.alternatives()
                                                           .conditional(
                                                               'operator',
                                                               {
                                                                   is       : Joi.string().valid( 'empty', 'notEmpty' ),
                                                                   then     : Joi.string().forbidden(),
                                                                   otherwise: Joi.string().required()
                                                               }
                                                           )
                                                           .description( 'The filter value.' ),
                                             valueType: Joi.alternatives()
                                                           .conditional(
                                                               'operator',
                                                               {
                                                                   is       : Joi.string().valid( 'empty', 'notEmpty' ),
                                                                   then     : Joi.string().forbidden(),
                                                                   otherwise: Joi.string().required().valid( ...filterValueTypes )
                                                               }
                                                           )
                                                           .description( 'The type for matching the filter value against data.' )
                                         } )
                                         .description( 'The descriptor for filtering the data.' );

export type SortDescriptor = {
    direction : SortDirection;
};
const sortDirections = enumerate<SortDirection>();
export const sortDescriptorSchema = Joi.object( {
                                           direction: Joi.string()
                                                         .required()
                                                         .valid( ...sortDirections )
                                                         .description( 'The direction in which the data is sorted.' )
                                       } )
                                       .description( 'The descriptor for sorting the data.' );

export const formatSchema = Joi.object( {
                                   type   : Joi.string()
                                               .required()
                                               .valid( ...formatTypes )
                                               .description( 'The type of the format to apply to the value.' ),
                                   prefix : Joi.string()
                                               .description( 'A string to be added before the value.' ),
                                   suffix : Joi.string()
                                               .description( 'A string to be added after the value.' ),
                                   locales: Joi.alternatives()
                                               .try(
                                                   Joi.string()
                                                      .description( 'A string with a BCP 47 language tag.' ),
                                                   Joi.array()
                                                      .items(
                                                          Joi.string()
                                                             .description( 'A string with a BCP 47 language tag.' )
                                                      )
                                                      .description( 'An array of strings with a BCP 47 language tag.' )
                                               )
                                               .description( 'A string with a BCP 47 language tag, or an array of such strings.<br/>Defaults to the users browser locale.' ),
                                   options: Joi.object()
                                               .description( 'Formatting options depending on <param>type</param>.' )
                               } )
                               .description( 'A format object to be applied to the content of the column according.' );

export const conditionSchema = Joi.object( {
                                      leftExpression              : Joi.alternatives()
                                                                       .conditional(
                                                                           'operator',
                                                                           {
                                                                               is       : Joi.string().valid( 'and', 'or' ),
                                                                               then     : Joi.array()
                                                                                             .description( 'An array of conditions.' ),
                                                                               otherwise: Joi.string()
                                                                                             .description( 'The static left expression.' )
                                                                           }
                                                                       )
                                                                       .description( 'The static left expression for the compare or if <param>operator</param> is set to \'and\' or \'or\' an array of conditions.' ),
                                      leftExpressionDataDescriptor: Joi.object( {
                                                                           alias: Joi.string()
                                                                                     .required()
                                                                                     .description( 'The key within the loaded data.' )
                                                                       } )
                                                                       .description( 'The target dataDescriptor to get dynamic left expression for the compare.' ),

                                      operator: Joi.string()
                                                   .valid( ...conditionOperator )
                                                   .description( 'The operator for matching the left expression against right expression with.' ),

                                      rightExpression              : Joi.alternatives()
                                                                        .conditional(
                                                                            'operator',
                                                                            {
                                                                                is       : Joi.string().valid( 'in', 'notIn' ),
                                                                                then     : Joi.array()
                                                                                              .description( 'An array of conditions.' ),
                                                                                otherwise: Joi.alternatives()
                                                                                              .conditional(
                                                                                                  'operator',
                                                                                                  {
                                                                                                      is       : Joi.string().invalid( 'and', 'or', 'empty', 'notEmpty' ),
                                                                                                      then     : Joi.string()
                                                                                                                    .description( 'The static right expression.' ),
                                                                                                      otherwise: Joi.forbidden()
                                                                                                  }
                                                                                              )
                                                                            }
                                                                        )
                                                                        .description( 'The static right expression for the compare or if <param>operator</param> is set to \'in\' or \'notIn\' an array of conditions or if <param>operator</param> is set to \'and\', \'or\', \'empty\' or \'notEmpty\' an array of conditions.' ),
                                      rightExpressionDataDescriptor: Joi.object( {
                                                                            field: Joi.string()
                                                                                      .required()
                                                                                      .description( 'The key within the loaded data.' )
                                                                        } )
                                                                        .description( 'The target dataDescriptor to get dynamic right expression for the compare.' )
                                  } )
                                  .description( 'A condition.' );

export const stylingObjectSchema = Joi.object( {
                                          className: Joi.string()
                                                        .description( 'A CSS class name to apply.' ),
                                          style    : Joi.object()
                                                        .description( 'A CSS style object to apply.' ),
                                          condition: conditionSchema
                                      } )
                                      .description( 'A conditional styling object.' );

export type Align = 'center' | 'left' | 'right';
export type ConfigDescriptor = {
    align? : Align;
    bodyClassName? : string;
    headerClassName? : string;
    filterHeaderClassName? : string;
    format? : Format;
    frozen? : boolean;
    header? : string;
    headerStyling? : Style[];
    hidable? : boolean;
    hidden? : boolean;
    reorderable? : boolean;
    resizeable? : boolean;
    searchColumn? : boolean;
    sortable? : boolean;
    styling? : Style[];
    width? : number | 'auto';
    minWidth? : number;
    maxWidth? : number;

    pt? : ColumnPassThroughOptions & {
        headerCell : ColumnPassThroughOptions['headerCell'] & {
            [ key : `data-${string}` ] : string;
        }
    };
};
const aligns = enumerate<Align>();
export const configDescriptorSchema = Joi.object( {
                                             align        : Joi.string()
                                                               .valid( ...aligns )
                                                               .default( 'left' )
                                                               .description( 'The align of the content within the column.' ),
                                             footer       : Joi.string()
                                                               .description( 'The columns footer.' ),
                                             format       : formatSchema,
                                             frozen       : Joi.boolean()
                                                               .default( false )
                                                               .description( 'Is the column is frozen.' ),
                                             header       : Joi.string()
                                                               .description( 'The columns header.' ),
                                             headerStyling: Joi.array()
                                                               .items(
                                                                   stylingObjectSchema
                                                               )
                                                               .description( 'An array of styling objects to manipulate the style of the columns header.' ),
                                             hidable      : Joi.boolean()
                                                               .default( true )
                                                               .description( 'Is the column is hidable.' ),
                                             hidden       : Joi.boolean()
                                                               .default( false )
                                                               .description( 'Is the column is hidden.' ),
                                             reorderable  : Joi.boolean()
                                                               .default( true )
                                                               .description( 'Is the column reorderable.' ),
                                             resizeable   : Joi.boolean()
                                                               .default( true )
                                                               .description( 'Is the column width resizable.' ),
                                             sortable     : Joi.boolean()
                                                               .default( true )
                                                               .description( 'Is the column sortable.' ),
                                             styling      : Joi.array()
                                                               .items(
                                                                   stylingObjectSchema
                                                               )
                                                               .description( 'An array of styling objects to manipulate the style of the column.' ),
                                             width        : Joi.number()
                                                               .unit( 'pixels' )
                                                               .description( 'The width of the column in pixels.' ),
                                             minWidth     : Joi.number()
                                                               .unit( 'pixels' )
                                                               .description( 'The minimum width of the column in pixels.<br/><br/>Only applicable if <strong>resizable</strong> is set to true.' ),
                                             maxWidth     : Joi.number()
                                                               .unit( 'pixels' )
                                                               .description( 'The maximum width of the column in pixels.<br/><br/>Only applicable if <strong>resizable</strong> is set to true.' )
                                         } )
                                         .description( 'The descriptor for the column configuration.' );

export type ColumnConfig = DataDescriptor & ConfigDescriptor;

export type DefinitionDescriptor = ConfigDescriptor & {
    header : string;
    footer? : string;
    dataDescriptor : ColumnDataDescriptor;
    permissions? : GroupPermissions;

    editor? : boolean;
    editorConfig? : EditorConfig;
    editorDefaultValue? : AttributeValue;
    editorHelpText? : string;
    editorValidatorRegExp? : string;
    editorValidationErrorText? : string;

    filter? : boolean;
    filterConfig? : FilterConfig;

    tooltip? : Message;

    valueType? : PropertyValueType;

    panelConfigIds? : number[];
};
export type ColumnDefinition = DataDescriptor & DefinitionDescriptor;
export const columnsSchema = Joi.array()
                                .items(
                                    Joi.object()
                                       .concat( dataDescriptorSchema )
                                       .concat( configDescriptorSchema )
                                )
                                .description( 'An array of columns the overview should contain.' );

export type ColumnFilter = DataDescriptor & FilterDescriptor;
export const filtersSchema = Joi.array()
                                .items(
                                    Joi.object()
                                       .concat( dataDescriptorSchema )
                                       .concat( filterDescriptorSchema )
                                )
                                .default( [] )
                                .description( 'An array of columns with filter information.' );

export type ColumnSorter = DataDescriptor & SortDescriptor;
export const sortersSchema = Joi.array()
                                .items(
                                    Joi.object()
                                       .concat( dataDescriptorSchema )
                                       .concat( sortDescriptorSchema )
                                )
                                .default( [] )
                                .description( 'An array of columns with sort information.' );

export type RowStyling = OptionalDataDescriptor & Style;
export const rowStylingSchema = Joi.array()
                                   .items(
                                       Joi.object()
                                          .concat( optionalDataDescriptorSchema )
                                          .concat( stylingObjectSchema )
                                   )
                                   .description( 'An array of styling objects to manipulate the style of the rows.' );

export const commonOverviewSchema = Joi.object( {
                                           searchString          : searchStringSchema,
                                           showPropertyDefaults  : showPropertyDefaultsSchema,
                                           rawMode               : rawModeSchema,
                                           showAdditionalRawNames: showAdditionalRawNamesSchema,
                                           page                  : pageSchema,
                                           rows                  : rowsSchema,
                                           search                : searchSchema,
                                           forceFit              : forceFitSchema,
                                           editable              : editableSchema,
                                           editMode              : editModeSchema
                                       } )
                                       .description( 'The common overview schema.' );

export const overviewSchema = Joi.object()
                                 .concat(
                                     commonOverviewSchema
                                 )
                                 .concat( Joi.object( {
                                     columns   : columnsSchema,
                                     fixFilters: filtersSchema,
                                     filters   : filtersSchema,
                                     sorters   : sortersSchema,
                                     rowStyling: rowStylingSchema
                                 } ) )
                                 .description( 'The full overview schema.' );
